import type { Language } from "@/lib/locale"

interface JwtPayload {
  [key: string]: unknown
  SecurityContactCultureId?: string
}

/**
 * Decodes a JWT payload for display/preference purposes only — the signature
 * is NOT verified here (the services do that on every call), so nothing
 * security-relevant may be decided from what this returns.
 */
export function decodeJwtToken(token: string): JwtPayload | null {
  try {
    // header.payload.signature
    const parts = token.split(".")
    if (parts.length !== 3) {
      console.warn("Invalid JWT token format")
      return null
    }

    const payload = parts[1]
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4)
    const decodedPayload = atob(paddedPayload.replace(/-/g, "+").replace(/_/g, "/"))

    return JSON.parse(decodedPayload) as JwtPayload
  } catch (error) {
    console.error("Error decoding JWT token:", error)
    return null
  }
}

/**
 * The operator's FBO language, from the token's `SecurityContactCultureId`
 * claim: '48' = en-CA, '88' = fr-CA. Everything else — '58' (en-US), a
 * missing claim, an undecodable token — is English, the same fallback the
 * other FBO microapps use.
 */
export function getLanguageFromToken(token: string): Language {
  const payload = decodeJwtToken(token)
  if (!payload) {
    console.warn("Could not decode JWT token, defaulting to English")
    return "en"
  }

  const cultureId = payload.SecurityContactCultureId
  if (!cultureId) {
    console.warn("SecurityContactCultureId not found in token, defaulting to English")
    return "en"
  }

  switch (String(cultureId)) {
    case "48":
      return "en"
    case "88":
      return "fr"
    default:
      console.warn(`Unknown SecurityContactCultureId: ${cultureId}, defaulting to English`)
      return "en"
  }
}
